import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Safe trim for nullable API and form fields — never throws on null or undefined. */
export function trimText(value: string | null | undefined): string {
  if (value == null) return "";
  return value.trim();
}

/** Trim and return null when the result is empty. */
export function trimTextOrNull(value: string | null | undefined): string | null {
  const trimmed = trimText(value);
  return trimmed === "" ? null : trimmed;
}

/** Em dash for empty table cells. Use this constant in source — not a literal “—” — so Windows PowerShell rewrites do not mojibake UTF-8. */
export const EM_DASH = "\u2014";

/** Horizontal ellipsis for loading and abbreviated copy. Prefer this over a literal “…” in source on Windows agent edits. */
export const HORIZONTAL_ELLIPSIS = "\u2026";

export function formatTimestamp(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function relativeTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const rawSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (rawSeconds < 60) return "<1m ago";
  if (rawSeconds < 3600) return `${Math.floor(rawSeconds / 60)}m ago`;
  if (rawSeconds < 86400) return `${Math.floor(rawSeconds / 3600)}h ago`;
  return `${Math.floor(rawSeconds / 86400)}d ago`;
}

/** Compact duration for run elapsed or total time (e.g. 45s, 3m 12s, 1h 5m). */
export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes < 60) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const minutesRem = minutes % 60;
  return minutesRem === 0 ? `${hours}h` : `${hours}h ${minutesRem}m`;
}

export function ingestRunDurationLabel(
  run: { started_at: string; ended_at: string | null; status: string },
  nowMs: number,
): { text: string; isLive: boolean } {
  const startMs = new Date(run.started_at).getTime();
  if (Number.isNaN(startMs)) return { text: "—", isLive: false };
  if (run.status === "running") {
    return { text: formatDurationMs(nowMs - startMs), isLive: true };
  }
  if (run.ended_at) {
    const endMs = new Date(run.ended_at).getTime();
    if (!Number.isNaN(endMs) && endMs >= startMs) {
      return { text: formatDurationMs(endMs - startMs), isLive: false };
    }
  }
  return { text: "—", isLive: false };
}
